import { FLOW_EDITING_TOKEN } from '../constants/graph';
import type { Filter, ItemCollectionResponse } from '../types/api';
import type { ElementResponseAPI } from '../types/element';
import type {
    ElementChange,
    FlowRequestAPI,
    FlowResponseAPI,
    PackageConflictResponse,
    ReleasesWithEnvironments,
} from '../types/flow';
import type {
    NavigationElementRequestAPI,
    NavigationElementResponseAPI,
} from '../types/navigation';
import type {
    Culture,
    CultureApi,
    TranslateFlowExportRequest,
    TranslateFlowImportRequest,
} from '../types/translation';
import {
    CheckStatus,
    fetchAndParse,
    fetchAndParseBlob,
    handleConflict,
    parse,
} from '../utils/ajax';
import { getAuthHeader } from '../utils/auth';
import { getTenantId } from '../utils/tenant';

interface SharedPackageRequestAPI {
    token: string;
}

interface SharedPackageResponseAPI extends SharedPackageRequestAPI {}

export const getActivatedFlow = async (flowId: string) => {
    const response = await fetchAndParse<FlowResponseAPI>({
        url: `/api/draw/1/flow/active/${flowId}`,
        headers: {
            ManyWhoTenant: getTenantId(),
        },
    });

    return response;
};

export const getActivated = async () => {
    const response = await fetchAndParse<FlowResponseAPI[]>({
        url: '/api/draw/1/flow/active',
        headers: {
            ManyWhoTenant: getTenantId(),
        },
    });

    return response;
};

export const getActivatedForEnvironment = async (environmentId: string) => {
    const response = await fetchAndParse<FlowResponseAPI[]>({
        url: `/api/draw/1/flow/active/environment/${environmentId}`,
        headers: {
            ManyWhoTenant: getTenantId(),
        },
    });

    return response;
};

export const getFlows = async () => {
    const response = await fetchAndParse<FlowResponseAPI[]>({
        url: '/api/draw/1/flow/',
        headers: {
            ManyWhoTenant: getTenantId(),
        },
    });

    return response;
};

export const getFlowsPaged = async (filter: Filter, tags: string[]) => {
    const params = new URLSearchParams();
    params.append('search', filter.search ?? '');
    params.append('page', filter.page?.toString() ?? '1');
    params.append('limit', filter.limit?.toString() ?? '20');
    params.append('orderBy', filter.orderBy ?? 'datemodified');
    params.append('orderDirection', filter.orderDirection ?? 'DESC');

    if (filter.isPublic !== undefined) {
        params.append('isPublic', filter.isPublic.toString());
    }

    tags.forEach((tag) => {
        params.append('tags', tag);
    });

    const response = await fetchAndParse<ItemCollectionResponse<FlowResponseAPI>>({
        url: `/api/draw/2/flow?${params.toString()}`,
        headers: {
            ManyWhoTenant: getTenantId(),
        },
    });

    return response;
};

export const getFlowLabels = async () => {
    const response = await fetchAndParse<string[]>({
        url: '/api/draw/2/flow/tags',
        headers: {
            ManyWhoTenant: getTenantId(),
        },
    });

    return response;
};

export const getLatest = async (id: string) => {
    const response = await fetchAndParse<FlowResponseAPI>({
        url: `/api/draw/1/flow/${id}`,
        headers: {
            ManyWhoTenant: getTenantId(),
        },
    });

    return response;
};

export const getSnapshots = async (id: string, page: number) => {
    const response = await fetchAndParse<ItemCollectionResponse<FlowResponseAPI>>({
        url: `/api/draw/2/flow/snap/${id}?page=${page}`,
        headers: {
            ManyWhoTenant: getTenantId(),
        },
    });

    return response;
};

export const getSnapshotsWithEnvironmentInfo = async (id: string) => {
    const response = await fetchAndParse<ReleasesWithEnvironments>({
        url: `/api/draw/1/flow/snap/environments/${id}`,
        headers: {
            ManyWhoTenant: getTenantId(),
        },
    });

    return response;
};

export const addElement = async (elementId: string, flowId: string, elementType: string) => {
    const response = await fetchAndParse<Response>({
        url: `/api/draw/1/element/flow/${flowId}/${elementType}/${elementId}`,
        method: 'post',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            ManyWhoTenant: getTenantId(),
        },
    });

    return response;
};

export const removeElement = async (elementId: string, flowId: string, elementType: string) => {
    const response = await fetchAndParse({
        url: `/api/draw/1/element/flow/${flowId}/${elementType}/${elementId}`,
        method: 'DELETE',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            ManyWhoTenant: getTenantId(),
        },
    });

    return response;
};

export const setLatest = async (flowData: FlowRequestAPI) => {
    const response = await fetchAndParse<FlowResponseAPI>({
        url: '/api/draw/1/flow',
        method: 'post',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            ManyWhoTenant: getTenantId(),
        },
        body: JSON.stringify(flowData),
    });

    return response;
};

export const activateFlowSnapshot = async (flowId: string, versionId: string) => {
    const response = await fetchAndParse<FlowResponseAPI>({
        url: `/api/draw/1/flow/activation/${flowId}/${versionId}/true/true`,
        method: 'post',
        headers: {
            ManyWhoTenant: getTenantId(),
        },
    });

    return response;
};

export const deactivateAllFlowSnapshots = async (flowId: string) => {
    await fetch(`/api/draw/1/flow/deactivate/${flowId}`, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            ManyWhoTenant: getTenantId(),
            ...(await getAuthHeader()),
        },
    });
};

export const exportFlow = (id: string, includePasswords: boolean, environmentId: string | null) => {
    let url = `/api/package/2/flow/${id}`;

    if (environmentId) {
        url += `/environment/${environmentId}`;
    }

    url += `?nullPasswords=${includePasswords ? 'false' : 'true'}`;

    return fetchAndParseBlob({
        url,
        headers: {
            ManyWhoTenant: getTenantId(),
        },
    });
};

export const shareFlow = async (
    id: string,
    includePasswords: boolean,
    environmentId: string | null,
) => {
    let url = `/api/package/1/flow/${id}/share`;

    if (environmentId) {
        url += `/environment/${environmentId}`;
    }

    url += `?nullPasswords=${includePasswords ? 'false' : 'true'}`;

    const response = await fetchAndParse<SharedPackageResponseAPI>({
        url,
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ManyWhoTenant: getTenantId(),
        },
    });

    return response;
};

export const importFlow = async (json: string, force: boolean, version = 1) => {
    const response = await fetch(
        `/api/package/${version}/flow${force ? '' : '?overwriteExisting=false'}`,
        {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                ManyWhoTenant: getTenantId(),
                ...(await getAuthHeader()),
            },
            body: json,
        },
    );

    await handleConflict(response);

    return (await parse(await CheckStatus(response))) as FlowResponseAPI;
};

export const importFlow2 = async (flowPackage: Blob, force: boolean) => {
    const response = await fetch(`/api/package/2/flow${force ? '' : '?overwriteExisting=false'}`, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/zip',
            ManyWhoTenant: getTenantId(),
            ...(await getAuthHeader()),
        },
        body: flowPackage,
    });

    await handleConflict(response);

    return (await parse(await CheckStatus(response))) as FlowResponseAPI;
};

export const importSharedFlow = async (token: string, force: boolean) => {
    const response = await fetch(
        `/api/package/1/shared/flow${force ? '' : '?overwriteExisting=false'}`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ManyWhoTenant: getTenantId(),
                ...(await getAuthHeader()),
            },
            body: JSON.stringify({
                token,
            }),
        },
    );

    await handleConflict(response);

    return (await parse(await CheckStatus(response))) as FlowResponseAPI;
};

export const getFlowSharedElements = async (flowId: string, elementType: string) => {
    const response = await fetchAndParse<ElementResponseAPI[]>({
        url: `/api/draw/1/element/flow/${flowId}/${elementType}`,
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            ManyWhoTenant: getTenantId(),
        },
    });

    return response;
};

export const deleteFlow = async (id: string) => {
    const response = await fetchAndParse<Response>({
        url: `/api/draw/1/flow/${id}`,
        method: 'DELETE',
        headers: {
            ManyWhoTenant: getTenantId(),
        },
    });

    return response;
};

export const getNavigationElement = async ({
    flowId,
    navigationElementId,
}: {
    flowId: string;
    navigationElementId: string;
}) => {
    const response = await fetchAndParse<NavigationElementResponseAPI>({
        url: `/api/draw/1/flow/${flowId}/${FLOW_EDITING_TOKEN}/element/navigation/${
            navigationElementId
        }`,
        method: 'GET',
        headers: {
            ManyWhoTenant: getTenantId(),
        },
    });

    return response;
};

export const getNavigationElements = async ({ flowId }: { flowId: string }) => {
    const response = await fetchAndParse<NavigationElementResponseAPI[]>({
        url: `/api/draw/1/flow/${flowId}/${FLOW_EDITING_TOKEN}/element/navigation/`,
        method: 'GET',
        headers: {
            ManyWhoTenant: getTenantId(),
        },
    });

    return response;
};

export const createOrUpdateNavigationElements = async (
    { flowId }: { flowId: string },
    navigationElement: NavigationElementRequestAPI,
) => {
    const response = await fetchAndParse<NavigationElementResponseAPI[]>({
        url: `/api/draw/1/flow/${flowId}/${FLOW_EDITING_TOKEN}/element/navigation`,
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            ManyWhoTenant: getTenantId(),
        },
        body: navigationElement,
    });

    return response;
};

export const deleteNavigationElement = async ({
    flowId,
    navigationElementId,
}: {
    flowId: string;
    navigationElementId: string;
}) => {
    const response = await fetchAndParse<Response>({
        url: `/api/draw/1/flow/${flowId}/${FLOW_EDITING_TOKEN}/element/navigation/${navigationElementId}`,
        method: 'DELETE',
        headers: {
            ManyWhoTenant: getTenantId(),
        },
    });

    return response;
};

export const exportFlowsForTranslation = async ({
    translateFlowExportRequest,
}: {
    translateFlowExportRequest: TranslateFlowExportRequest;
}) => {
    const response = await fetchAndParse<string[]>({
        url: '/api/translate/2/flows/export/',
        method: 'POST',
        headers: {
            ManyWhoTenant: getTenantId(),
        },
        body: JSON.stringify(translateFlowExportRequest),
    });

    let completedFlows = 0;

    response.forEach((flowTranslation, index) => {
        // If the xliff has a length of 0 then the Flow has already been translated and we should tell the user this instead of downloading the file.
        if (flowTranslation.length === 0) {
            completedFlows++;
        } else {
            const elem = window.document.createElement('a');
            elem.href = window.URL.createObjectURL(
                new Blob([flowTranslation], { type: 'text/xml' }),
            );
            elem.download = `flow-translation-export-${translateFlowExportRequest.targetLanguages[index]}.xlf`;
            document.body.appendChild(elem);
            elem.click();
            document.body.removeChild(elem);
        }
    });

    return { response, completedFlows };
};

export const importFlowTranslation = async ({
    translateFlowImportRequest,
}: {
    translateFlowImportRequest: TranslateFlowImportRequest;
}) => {
    const response = await fetchAndParse<Response>({
        url: '/api/translate/1/flows/import',
        method: 'POST',
        headers: {
            ManyWhoTenant: getTenantId(),
        },
        body: JSON.stringify(translateFlowImportRequest),
    });

    return response;
};

export const getTranslationLanguagesForFlow = async ({ flowId }: { flowId: string }) => {
    const response = await fetchAndParse<CultureApi[]>({
        url: `/api/translate/1/${flowId}/translations`,
        method: 'GET',
        headers: {
            ManyWhoTenant: getTenantId(),
        },
    });

    return response;
};

export const getTranslationLanguagesForFlows = async ({ flowIds }: { flowIds: string[] }) => {
    const response = await fetchAndParse<CultureApi[]>({
        url: `/api/translate/1/flows/translations`,
        method: 'POST',
        headers: {
            ManyWhoTenant: getTenantId(),
        },
        body: JSON.stringify(flowIds),
    });

    return response;
};

export const getDefaultCulture = async () => {
    const response = await fetchAndParse<CultureApi>({
        url: '/api/translate/1/default-culture',
        method: 'GET',
        headers: {
            ManyWhoTenant: getTenantId(),
        },
    });

    return response;
};

export const getAllCultures = async () => {
    const response = await fetchAndParse<Culture[]>({
        url: '/api/translate/1/cultures',
        method: 'GET',
        headers: {
            ManyWhoTenant: getTenantId(),
        },
    });

    return response;
};

export const getSnapshotDiffs = async (
    flowId: string,
    beforeVersionId: string,
    afterVersionId: string,
) => {
    const response = await fetchAndParse<ElementChange[]>({
        url: `/api/draw/1/flow/snap/${flowId}/diff/${beforeVersionId}/${afterVersionId}`,
        headers: {
            ManyWhoTenant: getTenantId(),
        },
    });

    return response;
};

export const revertFlow = async (
    flowId: string,
    versionId: string,
    force: boolean,
    comment: string,
) => {
    const response = await fetch(
        `/api/draw/2/flow/revert/${flowId}/${versionId}?overwriteExisting=${force}&automaticSnapshotComment=${comment}`,
        {
            method: 'POST',
            headers: {
                ManyWhoTenant: getTenantId(),
                ...(await getAuthHeader()),
            },
        },
    );

    return (await parse(await CheckStatus(await handleConflict(response)))) as
        | FlowResponseAPI
        | PackageConflictResponse;
};
